<template>
    <BaseCard v-if="dimension && current" as="section" padding="none" class="grid min-w-0 grid-cols-1 md:grid-cols-[12.5rem_minmax(0,1fr)]">
        <nav class="flex flex-col gap-1 border-b border-card-border p-3 md:border-r md:border-b-0" :aria-label="t('teamPage.lensNavigation')">
            <div v-for="item in dimensions" :key="item.id" class="flex flex-col gap-1">
                <BaseButton
                    theme="tertiary"
                    :class="{ 'bg-surface': dimension.id === item.id && !selectedFacet }"
                    shape="square"
                    :aria-expanded="dimension.id === item.id"
                    :aria-controls="`${navigationId}-${item.id}`"
                    :aria-pressed="dimension.id === item.id && !selectedFacet"
                    class="flex min-h-10 w-full items-center justify-between gap-2 px-3 py-2 text-left text-ink"
                    @click="selectDimension(item.id)">
                    <span :class="dimension.id === item.id ? 'body-small-bold' : 'body-small'">{{ dimensionTitles[item.id] ?? item.title }}</span>
                    <i class="bi text-ink-muted" :class="dimension.id === item.id ? 'bi-chevron-down' : 'bi-chevron-right'" aria-hidden="true" />
                </BaseButton>
                <div v-show="dimension.id === item.id" :id="`${navigationId}-${item.id}`" class="ml-5 flex flex-wrap gap-1 border-l border-card-border pl-3 md:flex-col">
                    <BaseButton
                        v-for="facet in item.lenses"
                        :key="facet.id"
                        theme="tertiary"
                        :class="{ 'bg-surface': selectedFacet === facet.id }"
                        shape="square"
                        size="sm"
                        :aria-pressed="dimension.id === item.id && selectedFacet === facet.id"
                        class="justify-start whitespace-normal px-3 py-2 text-left text-ink"
                        @click="selectFacet(facet.id)">
                        <span :class="selectedFacet === facet.id ? 'body-small-semibold' : 'body-small'">{{
                            lensTitles[facet.id] ?? t("perspective.team.lensFallback", { left: facet.left_label, right: facet.right_label })
                        }}</span>
                    </BaseButton>
                </div>
            </div>
        </nav>
        <div class="flex min-w-0 flex-col gap-5 p-4 md:p-6">
            <h2 class="body-serif text-ink">{{ questions[dimension.id] }}</h2>
            <TeamScoreDistribution :scores="current.scores" :members="members" :left-label="current.left_label" :right-label="current.right_label" :viewer-id="viewerId" />
            <div class="flex flex-col">
                <h3 class="body-small-semibold text-ink">{{ t("teamPage.whereTeamSits") }}</h3>
                <p class="body-small text-ink-muted">{{ t("teamPerspective.whereTeamSitsSummary", { reading, summary: teamSummary }) }}</p>
            </div>
            <div class="flex flex-col">
                <h3 class="body-small-semibold text-ink">{{ t("teamPerspective.tryTogether") }}</h3>
                <p class="body-small text-ink-muted">{{ suggestions[dimension.id] }}</p>
            </div>
            <div class="-mb-3 border-t border-card-border pt-2">
                <BaseAccordion
                    :accordion-ids="['guidance']"
                    class="-mx-2"
                    item-class="overflow-visible bg-transparent"
                    header-class="rounded-lgr"
                    trigger-class="min-h-0 rounded-lgr px-2 py-2 text-ink"
                    content-class="px-2 pb-3"
                    @update:model-value="guidanceChanged">
                    <template #trigger-guidance
                        ><span class="body-small-semibold">{{ t("teamPerspective.guidance") }}</span></template
                    >
                    <template #content-guidance>
                        <TeamPracticeGuidance :key="dimension.id" :guidance="dimension.guidance" />
                    </template>
                </BaseAccordion>
            </div>
        </div>
    </BaseCard>
</template>
<script setup lang="ts">
import type { TeamDimension, TeamPerspectiveMember } from "~vue/api/generated/team-perspective";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import TeamPracticeGuidance from "~vue/Perspective/TeamPracticeGuidance.vue";
import TeamScoreDistribution from "~vue/Perspective/TeamScoreDistribution.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ teamId: string; dimensions: TeamDimension[]; members: TeamPerspectiveMember[]; viewerId: string }>();
const { t } = useI18n();
const navigationId = useId();
const dimensionTitles = computed<Record<string, string>>(() => ({
    EI: t("teamPage.dimensionTitles.EI"),
    SN: t("teamPage.dimensionTitles.SN"),
    TF: t("teamPage.dimensionTitles.TF"),
    JP: t("teamPage.dimensionTitles.JP"),
}));
const questions = computed<Record<string, string>>(() => ({
    EI: t("teamPage.questions.EI"),
    SN: t("teamPage.questions.SN"),
    TF: t("teamPage.questions.TF"),
    JP: t("teamPage.questions.JP"),
}));
const suggestions = computed<Record<string, string>>(() => ({
    EI: t("teamPerspective.suggestions.energy"),
    SN: t("teamPerspective.suggestions.processing"),
    TF: t("teamPerspective.suggestions.decisions"),
    JP: t("teamPerspective.suggestions.structure"),
}));
const lensTitles = computed<Record<string, string>>(() => ({
    "initiating-receiving": t("teamPage.lenses.initiating-receiving"),
    "expressive-contained": t("teamPage.lenses.expressive-contained"),
    "gregarious-intimate": t("teamPage.lenses.gregarious-intimate"),
    "active-reflective": t("teamPage.lenses.active-reflective"),
    "enthusiastic-quiet": t("teamPage.lenses.enthusiastic-quiet"),
    "concrete-abstract": t("teamPage.lenses.concrete-abstract"),
    "realistic-imaginative": t("teamPage.lenses.realistic-imaginative"),
    "practical-conceptual": t("teamPage.lenses.practical-conceptual"),
    "experiential-theoretical": t("teamPage.lenses.experiential-theoretical"),
    "traditional-original": t("teamPage.lenses.traditional-original"),
    "logical-empathetic": t("teamPage.lenses.logical-empathetic"),
    "reasonable-compassionate": t("teamPage.lenses.reasonable-compassionate"),
    "questioning-accommodating": t("teamPage.lenses.questioning-accommodating"),
    "critical-accepting": t("teamPage.lenses.critical-accepting"),
    "tough-tender": t("teamPage.lenses.tough-tender"),
    "systematic-casual": t("teamPage.lenses.systematic-casual"),
    "planful-openended": t("teamPage.lenses.planful-openended"),
    "earlystarting-pressureprompted": t("teamPage.lenses.earlystarting-pressureprompted"),
    "scheduled-spontaneous": t("teamPage.lenses.scheduled-spontaneous"),
    "methodical-emergent": t("teamPage.lenses.methodical-emergent"),
}));
const selectedDimension = ref(props.dimensions[0]?.id);
const selectedFacet = ref<string | null>(null);
const dimension = computed(() => props.dimensions.find((item) => item.id === selectedDimension.value) ?? props.dimensions[0]);
const current = computed(() => dimension.value?.lenses.find((facet) => facet.id === selectedFacet.value) ?? dimension.value);
function guidanceChanged(value: string[] | string | undefined) {
    logUserInteraction(AmplitudeEventNames.TEAM_REPORT_CONTROL_SELECTED, {
        source: TEAM_INTERACTION_SOURCE.TEAM_PAGE,
        team_id: props.teamId,
        team_report_control: "guidance",
        expanded: Array.isArray(value) && value.includes("guidance"),
    });
}
function selectFacet(id: string) {
    if (selectedFacet.value === id) {
        return;
    }
    selectedFacet.value = id;
    logUserInteraction(AmplitudeEventNames.TEAM_REPORT_CONTROL_SELECTED, {
        source: TEAM_INTERACTION_SOURCE.TEAM_PAGE,
        team_id: props.teamId,
        team_report_control: "facet",
        report_facet: id,
    });
}
function selectDimension(id: string) {
    if (selectedDimension.value === id && !selectedFacet.value) {
        return;
    }
    logUserInteraction(AmplitudeEventNames.TEAM_REPORT_CONTROL_SELECTED, {
        source: TEAM_INTERACTION_SOURCE.TEAM_PAGE,
        team_id: props.teamId,
        team_report_control: "dimension",
        report_dimension: id,
    });
    selectedDimension.value = id;
    selectedFacet.value = null;
}
watch(dimension, (value) => {
    if (!value?.lenses.some((facet) => facet.id === selectedFacet.value)) {
        selectedFacet.value = null;
    }
});
const reading = computed(() => {
    const facet = current.value;
    if (!facet) {
        return "";
    }
    const average = facet.scores.length ? facet.scores.reduce((sum, score) => sum + score.score, 0) / facet.scores.length : 0;
    // Match the balanced threshold in coach_core/coach_core/team_perspective.py:team_spread.
    return Math.abs(average) < 0.5
        ? t("teamPerspective.spread.balanced")
        : t("teamPage.leans", { side: average < 0 ? facet.left_label.toLowerCase() : facet.right_label.toLowerCase() });
});
const teamSummary = computed(() => {
    const facet = current.value;
    if (!facet) {
        return "";
    }
    const scores = facet.scores;
    const left = scores.filter((score) => score.score < 0).length;
    const right = scores.filter((score) => score.score > 0).length;
    const balanced = scores.length - left - right;
    if (balanced > scores.length / 2) {
        return t("teamPerspective.balancedSummary", { count: balanced, total: scores.length });
    }
    if (left > scores.length / 2 || right > scores.length / 2) {
        return t("teamPerspective.directionSummary", {
            count: Math.max(left, right),
            total: scores.length,
            side: left > right ? facet.left_label.toLowerCase() : facet.right_label.toLowerCase(),
        });
    }
    return t("teamPerspective.mixedSummary");
});
</script>
